import { useEffect, useId, useState } from 'react';
import type { RefObject } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../components/Button';
import { Radio, RadioGroup } from '../../components/Radio';
import { TextInput } from '../../components/TextInput';
import {
  SetupInvitationError,
  acceptSetupInvitation,
} from '../../services/setupInvitations';
import type { InviteSport } from '../../types/adminInvites';
import type { SetupInvitationPreview } from '../../types/setupInvitations';
import { formatAnnualPrice, sportLabel } from '../signup/signupFormat';
import { InviteHeader, SignInLink } from './AcceptInviteLayout';
import type { SetupProgramOutcome } from './useSetupInvitationPreview';

/**
 * The half of the setup-program page that takes the invitee's details.
 *
 * Its own component so the form state lives and dies with the form, as `AcceptInviteForm` does.
 */

/** Mirrors the server's rules so the common mistakes never cost a round trip. */
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const MAX_NAME_LENGTH = 100;
const MAX_PROGRAM_NAME_LENGTH = 255;

const SPORTS: readonly InviteSport[] = ['baseball', 'softball'];

/** The fields a server refusal can land on, and how the alert summary names them. */
type ServerRefusedField = 'organizationName' | 'password';

const FIELD_LABELS: Record<ServerRefusedField, string> = {
  organizationName: 'Program name',
  password: 'Password',
};

const asSport = (value: string | null): InviteSport | undefined =>
  value === 'baseball' || value === 'softball' ? value : undefined;

type SetupProgramFormData = {
  firstName: string;
  lastName: string;
  password: string;
  confirmPassword: string;
  organizationName: string;
};

interface SetupProgramFormProps {
  token: string;
  preview: SetupInvitationPreview;
  headingRef: RefObject<HTMLHeadingElement | null>;
  /** Called only when the result ends the page. A refusal the invitee can fix keeps the form up. */
  onOutcome: (outcome: SetupProgramOutcome) => void;
}

export const SetupProgramForm = ({
  token,
  preview,
  headingRef,
  onOutcome,
}: SetupProgramFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [sport, setSport] = useState<InviteSport | undefined>(() => asSport(preview.sport));
  const [sportError, setSportError] = useState('');
  const emailHintId = useId();
  // A server refusal arrives while the inputs are still disabled, so focus waits for them. An
  // object, so a second refusal of the same field focuses it again.
  const [pendingFocus, setPendingFocus] = useState<{ field: ServerRefusedField } | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setError,
    setFocus,
    formState: { errors },
  } = useForm<SetupProgramFormData>({
    mode: 'onSubmit',
    defaultValues: { organizationName: preview.programName ?? '' },
  });

  const password = watch('password');

  useEffect(() => {
    if (isSubmitting || !pendingFocus) return;
    setFocus(pendingFocus.field);
  }, [isSubmitting, pendingFocus, setFocus]);

  /** Puts a server refusal on its field, names it in the alert, and focuses it once enabled. */
  const refuseField = (field: ServerRefusedField, message: string) => {
    setError(field, { message });
    setSubmitError(`${FIELD_LABELS[field]}: ${message}`);
    setPendingFocus({ field });
  };

  // The sport is outside react-hook-form, so a first submit with other errors still names it.
  const onInvalid = () => {
    setSubmitError('');
    if (!sport) setSportError('Choose a sport');
  };

  const onSubmit = async (data: SetupProgramFormData) => {
    setSubmitError('');
    setPendingFocus(null);
    if (!sport) {
      setSportError('Choose a sport');
      return;
    }
    setSportError('');
    setIsSubmitting(true);

    try {
      const accepted = await acceptSetupInvitation(token, {
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        password: data.password,
        organizationName: data.organizationName.trim(),
        sport,
      });
      onOutcome({ phase: 'accepted', accepted });
    } catch (error: unknown) {
      if (!(error instanceof SetupInvitationError)) {
        setSubmitError('Something went wrong. Your invitation link still works, so please try again.');
        return;
      }

      switch (error.kind) {
        case 'invalid':
          onOutcome({ phase: 'invalid' });
          break;
        case 'already_registered':
          onOutcome({ phase: 'registered', message: error.message, email: preview.email });
          break;
        case 'name_taken':
          refuseField('organizationName', error.message);
          break;
        case 'invalid_input':
          // Put the refusal on the field it is about when the server says which one.
          if (error.code === 'invalid_program_name') {
            refuseField('organizationName', error.message);
          } else if (error.code === 'invalid_password') {
            refuseField('password', error.message);
          } else if (error.code === 'invalid_sport') {
            setSportError(error.message);
          } else {
            setSubmitError(error.message);
          }
          break;
        // 429 and 500 leave the invitation usable, so the form stays and says why.
        default:
          setSubmitError(error.message);
          break;
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const priceLine =
    preview.requiresPayment && preview.annualPriceCents !== null
      ? `After you sign in, you pay ${formatAnnualPrice(preview.annualPriceCents, 'usd')} to activate it.`
      : preview.requiresPayment
        ? 'After you sign in, you pay to activate it.'
        : null;

  return (
    <>
      <InviteHeader
        title="Set up your program"
        subtitle={
          preview.schoolName
            ? `Create your account and your ${preview.schoolName} program.`
            : 'Create your account and your program.'
        }
        headingRef={headingRef}
      />

      <form className="accept-invite-form" onSubmit={handleSubmit(onSubmit, onInvalid)} noValidate>
        {submitError && (
          <div className="accept-invite-message accept-invite-message--error" role="alert">
            <p>{submitError}</p>
          </div>
        )}

        {priceLine && (
          <p className="accept-invite-hint" data-testid="setup-price-line">
            {priceLine}
          </p>
        )}

        <div className="accept-invite-inputs">
          <TextInput
            label="First name"
            autoComplete="given-name"
            error={errors.firstName?.message}
            disabled={isSubmitting}
            data-testid="first-name-input"
            {...register('firstName', {
              required: 'Enter your first name',
              validate: (v) => v.trim().length > 0 || 'Enter your first name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `First name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Last name"
            autoComplete="family-name"
            error={errors.lastName?.message}
            disabled={isSubmitting}
            data-testid="last-name-input"
            {...register('lastName', {
              required: 'Enter your last name',
              validate: (v) => v.trim().length > 0 || 'Enter your last name',
              maxLength: {
                value: MAX_NAME_LENGTH,
                message: `Last name must be ${MAX_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <div>
            {/* Read-only: the account is created for the invited address whatever this says. */}
            <TextInput
              label="Email"
              type="email"
              value={preview.email}
              readOnly
              aria-describedby={emailHintId}
              data-testid="invited-email"
            />
            <p className="accept-invite-hint" id={emailHintId}>
              Your account is created for this address.
            </p>
          </div>

          <TextInput
            label="Password"
            type="password"
            autoComplete="new-password"
            error={errors.password?.message}
            disabled={isSubmitting}
            data-testid="password-input"
            {...register('password', {
              required: 'Choose a password',
              minLength: {
                value: MIN_PASSWORD_LENGTH,
                message: `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
              },
              maxLength: {
                value: MAX_PASSWORD_LENGTH,
                message: `Password must be ${MAX_PASSWORD_LENGTH} characters or fewer`,
              },
            })}
          />

          <TextInput
            label="Confirm password"
            type="password"
            autoComplete="new-password"
            error={errors.confirmPassword?.message}
            disabled={isSubmitting}
            data-testid="confirm-password-input"
            {...register('confirmPassword', {
              required: 'Confirm your password',
              validate: (value) => value === password || 'Passwords do not match',
            })}
          />

          <TextInput
            label="Program name"
            autoComplete="organization"
            error={errors.organizationName?.message}
            disabled={isSubmitting}
            data-testid="program-name-input"
            {...register('organizationName', {
              required: 'Enter a program name',
              validate: (v) => v.trim().length > 0 || 'Enter a program name',
              maxLength: {
                value: MAX_PROGRAM_NAME_LENGTH,
                message: `Program name must be ${MAX_PROGRAM_NAME_LENGTH} characters or fewer`,
              },
            })}
          />

          <div>
            <RadioGroup label="Sport" testId="setup-sports">
              {SPORTS.map((option) => (
                <Radio
                  key={option}
                  name="sport"
                  value={option}
                  label={sportLabel(option)}
                  checked={sport === option}
                  onChange={() => {
                    setSport(option);
                    setSportError('');
                  }}
                  disabled={isSubmitting}
                  aria-invalid={sportError ? true : undefined}
                  data-testid={`setup-sport-${option}`}
                />
              ))}
            </RadioGroup>
            {sportError && (
              <p className="accept-invite-field-error" role="alert">
                {sportError}
              </p>
            )}
          </div>
        </div>

        <div className="accept-invite-actions">
          <Button type="submit" fullWidth loading={isSubmitting} data-testid="create-program-button">
            Create program
          </Button>

          <SignInLink>Already have an account? Sign in</SignInLink>
        </div>
      </form>
    </>
  );
};
